/**
 * API service - Using Axios
 */
import axiosInstance from './services/axiosService';

const normalizeProjectSlug = (rawSlug) => {
  if (rawSlug === null || rawSlug === undefined) return '';

  let value = String(rawSlug).trim();
  if (!value) return '';

  try {
    value = decodeURIComponent(value);
  } catch {
    // Keep original value when decoding fails.
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    try {
      value = new URL(value).hostname;
    } catch {
      // Ignore and keep current value.
    }
  }

  value = value.replace(/^\/+|\/+$/g, '').toLowerCase();
  return value.replace(/[^a-z0-9-]/g, '');
};

// Helper to handle auth errors
export const handleAuthError = (error) => {
  if (error.response) {
    const status = error.response.status;
    const errorDetail = error.response?.data?.detail || '';

    // Seulement rediriger pour les vraies erreurs d'authentification
    if (status === 401) {
      // 401 = toujours une erreur d'authentification
      localStorage.removeItem('jwt');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error('Session expirée');
    }

    // Pour 403, vérifier si c'est une erreur d'authentification ou de configuration
    if (status === 403) {
      // Erreurs d'authentification qui nécessitent une déconnexion
      const isAuthError = errorDetail.includes('Invalid token') ||
        errorDetail.includes('credentials') ||
        errorDetail.includes('authentication') ||
        errorDetail.includes('Unauthorized');

      if (isAuthError) {
        localStorage.removeItem('jwt');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Session expirée');
      }
      // Sinon, c'est une erreur de configuration (ex: clé Gemini), laisser le composant gérer
    }
  }
  throw error;
};

// Auth endpoints
export const login = async (email, password) => {
  try {
    // OAuth2PasswordRequestForm attend application/x-www-form-urlencoded
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    const response = await axiosInstance.post('/api/accounts/login', params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    // Le token sera stocké dans Login.jsx via setToken()
    // Ne pas le stocker ici pour éviter les doublons
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const register = async (userData) => {
  try {
    const response = await axiosInstance.post('/api/accounts/register', userData);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/me');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const updateUser = async (userData) => {
  try {
    const response = await axiosInstance.patch('/api/accounts/user/', userData, {
      headers: {
        'Content-Type': userData instanceof FormData ? 'multipart/form-data' : 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getOnboardingStatus = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/onboarding/status');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const deleteGeminiKey = async () => {
  try {
    await axiosInstance.delete('/api/accounts/gemini/key');
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const deletePageSpeedKey = async () => {
  try {
    await axiosInstance.delete('/api/accounts/pagespeed/key');
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Semrush endpoints
export const getSemrushKey = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/semrush/key');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const setSemrushKey = async (key) => {
  try {
    const response = await axiosInstance.post('/api/accounts/semrush/key', { key });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const deleteSemrushKey = async () => {
  try {
    await axiosInstance.delete('/api/accounts/semrush/key');
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getSemrushCredits = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/semrush/credits');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// SMTP (emails prospects / audits)
export const getSmtpConfig = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/smtp/config');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const setSmtpConfig = async (data) => {
  try {
    const response = await axiosInstance.post('/api/accounts/smtp/config', data);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const deleteSmtpConfig = async () => {
  try {
    await axiosInstance.delete('/api/accounts/smtp/config');
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const fetchSemrushData = async (slug) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/fetch-semrush`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Projects endpoints
export const getProjects = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/projects');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Optimized endpoints for better performance
export const getProjectsSummary = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/projects/summary');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getDashboardStats = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/dashboard/stats');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getProject = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const createProject = async (projectData) => {
  try {
    const response = await axiosInstance.post('/api/accounts/projects', projectData);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const updateProject = async (slug, projectData) => {
  try {
    const response = await axiosInstance.put(`/api/accounts/projects/${slug}`, projectData);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const deleteProject = async (slug) => {
  try {
    await axiosInstance.delete(`/api/accounts/projects/${slug}`);
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Audit endpoints
export const getAudits = async (slug, options = {}) => {
  try {
    const params = options.cacheBust ? { _t: Date.now() } : undefined;
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/audits`, { params });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getAuditByUrl = async (slug, url, options = {}) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/audit-by-url`, {
      params: { url },
      timeout: options.timeout ?? 60000
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    handleAuthError(error);
    throw error;
  }
};

export const getTechnicalErrors = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/technical-errors`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const analyseUrl = async (slug, url, skipPagespeed = false) => {
  try {
    const response = await axiosInstance.post(
      `/api/accounts/projects/${slug}/analyse-url`,
      { url, skip_pagespeed: skipPagespeed },
      { timeout: 120000 }
    );
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const auditProject = async (slug) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/audit`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const recalculateScores = async (slug) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/recalculate-scores`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getSitemap = async (slug, options = {}) => {
  try {
    const params = options.cacheBust ? { _t: Date.now() } : undefined;
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/sitemap`, { params });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getPageContent = async (slug, url) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/page-content`, { url });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const extractTechnicalInfo = async (slug, url) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/extract-technical-info`, { url });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const extractMenu = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/extract-menu`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const launchVisualAudit = async (slug, urls) => {
  try {
    const response = await axiosInstance.post(
      `/api/accounts/projects/${slug}/visual-audit`,
      { urls },
      { timeout: 180000 },
    );
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// PageSpeed endpoints
export const getPageSpeedKey = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/pagespeed/key');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const setPageSpeedKey = async (key) => {
  try {
    const response = await axiosInstance.post('/api/accounts/pagespeed/key', { key });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Gemini endpoints
export const getGeminiModels = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/gemini/models');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getGeminiKey = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/gemini/key');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const setGeminiKey = async (key) => {
  try {
    const response = await axiosInstance.post('/api/accounts/gemini/key', { key });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const testGeminiKey = async (prompt, key = null) => {
  try {
    const body = { prompt };
    if (key != null && String(key).trim()) body.key = key.trim();
    const response = await axiosInstance.post('/api/accounts/gemini/test', body);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const geminiAnalyse = async (prompt, model) => {
  try {
    const response = await axiosInstance.post('/api/accounts/gemini/analyse', { prompt, model });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Positioning analysis endpoints
export const getPositioningAnalyses = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/positioning-analysis`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const createPositioningAnalysis = async (slug, data) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/positioning-analysis`, data);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const checkFilesOnSite = async (slug) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/positioning-analysis/check-files`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

/** Comment le projet ressort dans les modèles IA (ex. Gemini) */
export const getAIVisibility = async (slug) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/positioning-analysis/ai-visibility`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// IA Analysis endpoints
export const getIAAnalysis = async (slug, url) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/ia-analyse`, {
      params: { url }
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const createIAAnalysis = async (slug, data) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/ia-analyse`, data);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

/** Régénère un seul bloc de suggestions (titre, meta_description ou h1) sans relancer l'analyse complète. */
export const regenerateSuggestionBlock = async (slug, { url, block }) => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/ia-analyse/regenerate`, { url, block });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

/**
 * Récupère les todo-lists IA en cache pour toutes les pages de référence.
 * Map { url normalisée: { summary, todos, generated_at, source_audit_date, stale } }.
 * Aucun appel IA côté backend (lecture cache). 404 → {}.
 */
export const getReferencePageTodos = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/reference-page-todos`);
    return response.data || {};
  } catch (error) {
    if (error.response?.status === 404) {
      return {};
    }
    handleAuthError(error);
    throw error;
  }
};

/**
 * Génère (ou régénère) la todo-list IA d'une page de référence via Gemini.
 * Appel long (génération IA) → timeout étendu. 403 = clé Gemini manquante.
 */
export const generateReferencePageTodo = async (slug, url) => {
  try {
    const response = await axiosInstance.post(
      `/api/accounts/projects/${slug}/reference-page-todos`,
      { url },
      { timeout: 120000 }
    );
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

/** Génère des suggestions d'alt pour les images sans alt (Gemini). */
export const generateAltSuggestions = async (slug, pageUrl, images) => {
  try {
    const response = await axiosInstance.post(
      `/api/accounts/projects/${slug}/generate-alt-suggestions`,
      {
        page_url: pageUrl,
        images: (images || []).map((img) => ({
          src: img.src,
          context_text: img.context_text || null,
          context_tag: img.context_tag || null,
        })),
      }
    );
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// AI Recommendations endpoints
export const getAIRecommendations = async (slug, generate = false) => {
  const normalizedSlug = normalizeProjectSlug(slug);
  if (!normalizedSlug) return null;

  try {
    if (generate) {
      // POST pour générer/régénérer les recommandations
      const response = await axiosInstance.post(`/api/accounts/projects/${normalizedSlug}/ai-recommendations?force=true`);
      return response.data;
    } else {
      // GET pour récupérer les recommandations existantes
      const response = await axiosInstance.get(`/api/accounts/projects/${normalizedSlug}/ai-recommendations`);
      return response.data;
    }
  } catch (error) {
    // Endpoint optionnel selon la version backend / état du projet.
    if (error?.response?.status === 404) {
      return null;
    }
    handleAuthError(error);
    throw error;
  }
};

// Test API key endpoint
export const testApiKey = async (apiKey, service, field = null, testType = 'format') => {
  try {
    const response = await axiosInstance.post('/api/accounts/test-api-key/', {
      api_key: apiKey,
      service: service,
      field: field,
      test_type: testType
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};


// Maillage interne
export const getInternalLinks = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/internal-links`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Contenu dupliqué
export const getDuplicateContent = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/duplicate-content`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// Liens brisés (à la demande)
export const checkBrokenLinks = async (slug, maxLinks = 100) => {
  try {
    const response = await axiosInstance.post(
      `/api/accounts/projects/${slug}/check-broken-links`,
      null,
      { params: { max_links: maxLinks }, timeout: 180000 }
    );
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// GEO / Positioning analysis — nouveaux endpoints
export const getLLMVisibilityTest = async (slug) => {
  try {
    const response = await axiosInstance.post(
      `/api/accounts/projects/${slug}/positioning-analysis/llm-visibility-test`,
      null,
      { timeout: 120000 }
    );
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getGeminiUsage = async () => {
  try {
    const response = await axiosInstance.get('/api/accounts/gemini/usage');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const resetGeminiUsage = async () => {
  try {
    const response = await axiosInstance.delete('/api/accounts/gemini/usage');
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Membres de projet (collaboration / équipe)
// ---------------------------------------------------------------------------

export const getProjectMembers = async (slug) => {
  try {
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/members`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const getProjectMemberCandidates = async (slug, query = '', limit = 12) => {
  try {
    const params = new URLSearchParams();
    if (query?.trim()) params.set('q', query.trim());
    if (limit) params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await axiosInstance.get(`/api/accounts/projects/${slug}/member-candidates${suffix}`);
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const inviteProjectMember = async (slug, email, role = 'viewer') => {
  try {
    const response = await axiosInstance.post(`/api/accounts/projects/${slug}/members`, { email, role });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const updateProjectMemberRole = async (slug, userId, role) => {
  try {
    const response = await axiosInstance.put(`/api/accounts/projects/${slug}/members/${userId}`, { role });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

export const removeProjectMember = async (slug, userId) => {
  try {
    await axiosInstance.delete(`/api/accounts/projects/${slug}/members/${userId}`);
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};
